// src/services/AdminAuthService.ts
/**
 * Admin Authentication Service
 * Uses Supabase Auth with admin email whitelist and optional role metadata.
 *
 * IMPORTANT: For production, implement proper RBAC with Supabase RLS and custom claims
 * or a dedicated admin_users table.
 */

import { supabase } from '../config/supabase';

const FALLBACK_ADMIN_EMAILS = ['admin@tradingspace.com'];

const getConfiguredAdminEmails = (): string[] => {
  const raw =
    process.env.REACT_APP_ADMIN_EMAILS ||
    process.env.REACT_APP_ADMIN_EMAIL_WHITELIST ||
    '';

  const parsed = raw
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  return parsed.length > 0 ? parsed : FALLBACK_ADMIN_EMAILS;
};

export class AdminAuthService {
  private static setAdminSession(email: string): void {
    sessionStorage.setItem('admin_session', 'true');
    sessionStorage.setItem('admin_email', email);
    sessionStorage.setItem('admin_login_time', new Date().toISOString());
  }

  private static clearAdminSession(): void {
    sessionStorage.removeItem('admin_session');
    sessionStorage.removeItem('admin_email');
    sessionStorage.removeItem('admin_login_time');
  }

  private static hasAdminPrivileges(user: any): boolean {
    const email = typeof user?.email === 'string' ? user.email.toLowerCase() : '';
    const configuredEmails = getConfiguredAdminEmails();

    // app_metadata ONLY. `user_metadata` is writable by the account owner via
    // supabase.auth.updateUser({ data: { role: 'admin' } }), so reading it here
    // let any registered user promote themselves into the admin area — the same
    // trap the plan resolution already avoids for subscriptions.
    //
    // This check is convenience, not security: it decides what the UI shows.
    // The data itself is gated server-side by the `admin-users` edge function
    // and migration 062, because a browser-side check is bypassable by
    // definition.
    const directRole = user?.app_metadata?.role;
    const appRoles = Array.isArray(user?.app_metadata?.roles) ? user.app_metadata.roles : [];

    const normalizedRoles = [directRole, ...appRoles]
      .filter(Boolean)
      .map((role) => String(role).toLowerCase());

    return normalizedRoles.includes('admin') || configuredEmails.includes(email);
  }

  /**
   * Check if current user is admin
   */
  static async isAdmin(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? (await supabase.auth.getUser()).data.user;

      if (!user || !user.email) {
        return false;
      }

      return this.hasAdminPrivileges(user);
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  /**
   * Login as admin (uses Supabase Auth)
   */
  static async loginAdmin(email: string, password: string): Promise<{
    success: boolean;
    error?: string;
    isAdmin?: boolean;
  }> {
    try {
      // Login with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data.user || !data.user.email) {
        return { success: false, error: 'Login failed' };
      }

      const isAdmin = this.hasAdminPrivileges(data.user);

      if (!isAdmin) {
        await supabase.auth.signOut();
        this.clearAdminSession();
        return {
          success: false,
          error: 'Access denied. This account does not have admin privileges.',
          isAdmin: false
        };
      }

      this.setAdminSession(data.user.email);
      return { success: true, isAdmin: true };
    } catch (error: any) {
      console.error('Admin login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  }

  /**
   * Logout admin
   */
  static async logoutAdmin(): Promise<void> {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAdminSession();
    }
  }

  /**
   * Get current admin info
   */
  static async getCurrentAdmin(): Promise<{
    email: string | null;
    loginTime: string | null;
  }> {
    const isAdmin = sessionStorage.getItem('admin_session') === 'true';

    if (!isAdmin) {
      return { email: null, loginTime: null };
    }

    return {
      email: sessionStorage.getItem('admin_email'),
      loginTime: sessionStorage.getItem('admin_login_time')
    };
  }

  /**
   * Validate admin session (checks both sessionStorage and Supabase auth)
   */
  static async validateSession(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? (await supabase.auth.getUser()).data.user;

      if (!user || !user.email || !this.hasAdminPrivileges(user)) {
        this.clearAdminSession();
        return false;
      }

      this.setAdminSession(user.email);
      return true;
    } catch (error) {
      console.error('Admin session validation error:', error);
      this.clearAdminSession();
      return false;
    }
  }

  /**
   * Add admin email to whitelist (temporary - should be in database)
   * Admin only - requires current user to be admin
   */
  static async addAdminEmail(email: string): Promise<boolean> {
    const isCurrentAdmin = await this.isAdmin();

    if (!isCurrentAdmin) {
      console.error('Only admins can add other admins');
      return false;
    }

    console.warn(`Admin email add requested for ${email}, but the current implementation is env/metadata based and not persisted.`);
    return false;
  }

  /**
   * Get admin emails (for display only)
   */
  static getAdminEmails(): string[] {
    return [...getConfiguredAdminEmails()];
  }
}
